import { AccountType } from "@common/constants/domains/accounting/accounts";
import { ChartOfAccountsTemplateVariableKind } from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import {
    ACCOUNT_ROLE_COPY,
    ACCOUNT_ROLE_ELIGIBLE_TYPES,
    isAccountTypeEligibleForRole,
} from "@common/constants/domains/accounting/accountRole";
import { DefaultKickAccount } from "@common/constants/domains/accounting/defaultAccountNames";
import {
    getKickDefaultRoleHolder,
    getKickRoleHoldersForEntityLegalType,
} from "@common/constants/domains/accounting/kickNativeAccountRole";
import { createTemplateCodeClaimer } from "@common/helpers/domains/accounting/templateAccountCodes";
import { normalizeMatchableName } from "@common/helpers/string";
import { AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";
import { allocateNextTemplateCodeText } from "../editor/draftCodes";

export interface RoleMappingAccount {
    id: string;
    name: string;
    codeText: string | null;
    type: AccountType;
    role: AccountRole | null;
}

export interface RequiredAccountRow {
    id: AccountRole;
    role: AccountRole;
    requiredAccountName: string;
    description: string;
    assignedAccountId: string | null;
    candidateAccounts: RoleMappingAccount[];
    canCreateDefaultAccount: boolean;
}

export interface CreateRoleAccountValues {
    role: AccountRole;
    name: string;
    type: AccountType;
    codeText: string | null;
}

const isNameMatch = (left: string, right: string): boolean =>
    normalizeMatchableName(left) === normalizeMatchableName(right);

export function buildRequiredAccountRows({
    accounts,
    nameContextAccounts,
    entityLegalType,
}: {
    accounts: readonly RoleMappingAccount[];
    nameContextAccounts: ReadonlyArray<Pick<RoleMappingAccount, "name">>;
    entityLegalType: EntityProfileType;
}): RequiredAccountRow[] {
    return getKickRoleHoldersForEntityLegalType(entityLegalType).map(
        ({ role, defaultHolder }) => {
            const assignedAccountId =
                accounts.find((account) => account.role === role)?.id ?? null;
            return {
                id: role,
                role,
                requiredAccountName: defaultHolder.name,
                description: ACCOUNT_ROLE_COPY[role].description,
                assignedAccountId,
                candidateAccounts: accounts.filter(
                    (account) =>
                        (account.role === null || account.role === role) &&
                        isAccountTypeEligibleForRole(role, account.type),
                ),
                canCreateDefaultAccount:
                    assignedAccountId === null &&
                    !nameContextAccounts.some((account) =>
                        isNameMatch(account.name, defaultHolder.name),
                    ),
            };
        },
    );
}

export function getDefaultRoleAccount({
    role,
    entityLegalType,
}: {
    role: AccountRole;
    entityLegalType: EntityProfileType;
}): DefaultKickAccount {
    const defaultHolder = getKickDefaultRoleHolder({ role, entityLegalType });
    return {
        name: defaultHolder?.name ?? "",
        type: defaultHolder?.type ?? ACCOUNT_ROLE_ELIGIBLE_TYPES[role][0],
    };
}

export function assignRole<T extends RoleMappingAccount>({
    accounts,
    role,
    accountId,
}: {
    accounts: readonly T[];
    role: AccountRole;
    accountId: string;
}): T[] {
    return accounts.map((account) => {
        if (account.id === accountId) {
            return { ...account, role };
        }
        return account.role === role ? { ...account, role: null } : account;
    });
}

const CREATED_ROLE_ACCOUNT_ID_PREFIX = "created:";

export const isCreatedRoleAccountId = (id: string): boolean =>
    id.startsWith(CREATED_ROLE_ACCOUNT_ID_PREFIX);

export function suggestCreatedRoleAccountCodeText({
    type,
    accounts,
}: {
    type: AccountType;
    accounts: ReadonlyArray<Pick<RoleMappingAccount, "id" | "codeText">>;
}): string | null {
    const hasCodesOnAllExisting = accounts.every(
        ({ id, codeText }) =>
            isCreatedRoleAccountId(id) ||
            (codeText !== null && codeText.trim() !== ""),
    );
    return hasCodesOnAllExisting
        ? allocateNextTemplateCodeText({ type, accounts })
        : null;
}

export function isCodeTextTaken({
    codeText,
    accounts,
}: {
    codeText: string;
    accounts: ReadonlyArray<Pick<RoleMappingAccount, "codeText">>;
}): boolean {
    const claimCode = createTemplateCodeClaimer();
    accounts.forEach((account) => claimCode(account.codeText));
    return !claimCode(codeText);
}

export function buildDefaultRoleAccountValue({
    role,
    entityLegalType,
    codeContextAccounts,
}: {
    role: AccountRole;
    entityLegalType: EntityProfileType;
    codeContextAccounts: ReadonlyArray<
        Pick<RoleMappingAccount, "id" | "codeText">
    >;
}): CreateRoleAccountValues {
    const defaultAccount = getDefaultRoleAccount({ role, entityLegalType });
    return {
        role,
        ...defaultAccount,
        codeText: suggestCreatedRoleAccountCodeText({
            type: defaultAccount.type,
            accounts: codeContextAccounts,
        }),
    };
}

export function buildDefaultRoleAccountValues({
    rows,
    entityLegalType,
    codeContextAccounts,
}: {
    rows: readonly RequiredAccountRow[];
    entityLegalType: EntityProfileType;
    codeContextAccounts: ReadonlyArray<
        Pick<RoleMappingAccount, "id" | "codeText">
    >;
}): CreateRoleAccountValues[] {
    return rows
        .filter(({ canCreateDefaultAccount }) => canCreateDefaultAccount)
        .reduce<{
            values: CreateRoleAccountValues[];
            takenAccounts: Array<Pick<RoleMappingAccount, "id" | "codeText">>;
        }>(
            ({ values, takenAccounts }, { role }) => {
                const value = buildDefaultRoleAccountValue({
                    role,
                    entityLegalType,
                    codeContextAccounts: takenAccounts,
                });
                return {
                    values: [...values, value],
                    takenAccounts: [
                        ...takenAccounts,
                        {
                            id: `${CREATED_ROLE_ACCOUNT_ID_PREFIX}${role}`,
                            codeText: value.codeText,
                        },
                    ],
                };
            },
            { values: [], takenAccounts: [...codeContextAccounts] },
        ).values;
}

export function appendCreatedRoleAccount<T extends RoleMappingAccount>({
    accounts,
    role,
    buildAccount,
}: {
    accounts: readonly T[];
    role: AccountRole;
    buildAccount: (id: string) => T;
}): T[] {
    const id = `${CREATED_ROLE_ACCOUNT_ID_PREFIX}${crypto.randomUUID()}`;
    return assignRole({
        accounts: [...accounts, buildAccount(id)],
        role,
        accountId: id,
    });
}

export function dropUnassignedCreatedAccounts<T extends RoleMappingAccount>(
    accounts: readonly T[],
): T[] {
    return accounts.filter(
        (account) =>
            account.role !== null || !isCreatedRoleAccountId(account.id),
    );
}

export function prefillRolesByDefaultName<T extends RoleMappingAccount>({
    accounts,
    entityLegalType,
}: {
    accounts: readonly T[];
    entityLegalType: EntityProfileType;
}): T[] {
    return getKickRoleHoldersForEntityLegalType(entityLegalType).reduce<T[]>(
        (current, { role, defaultHolder }) => {
            if (current.some((account) => account.role === role)) {
                return current;
            }
            const match = current.find(
                (account) =>
                    account.role === null &&
                    isAccountTypeEligibleForRole(role, account.type) &&
                    isNameMatch(account.name, defaultHolder.name),
            );
            return match
                ? assignRole({ accounts: current, role, accountId: match.id })
                : current;
        },
        [...accounts],
    );
}

export function canTakeRole({
    account,
    saved,
}: {
    account: { type: AccountType; isDisabled: boolean };
    saved:
        | {
              type: AccountType;
              variableKind: ChartOfAccountsTemplateVariableKind | null;
          }
        | undefined;
}): boolean {
    // The save rejects a role on a row whose type changed since the last
    // save, so a retyped row cannot take a role until the draft is saved.
    return (
        !account.isDisabled &&
        saved?.variableKind == null &&
        (saved === undefined || saved.type === account.type)
    );
}
